import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import { 
  Sun, Moon, Zap, Send, Mic, MicOff, Volume2, VolumeX,
  Plus, MessageSquare, Loader2, ChevronLeft, Bookmark, Check, AlertCircle, RotateCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { format } from 'date-fns';
import { toast } from 'sonner';

const Chat = () => {
  const { api, user } = useAuth();
  const { t, lang, translations: trans } = useTranslation();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [mode, setMode] = useState('decision');
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceAvailable, setVoiceAvailable] = useState(false);
  const [insightModal, setInsightModal] = useState({ open: false, content: '' });
  const [insightCategory, setInsightCategory] = useState('learning');
  const [savingInsight, setSavingInsight] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  useEffect(() => {
    const init = async () => {
      try {
        const [convsRes, modeRes, voiceRes] = await Promise.all([
          api.get('/api/conversations'),
          api.get('/api/suggested-mode'),
          api.get('/api/voice/status')
        ]);
        setConversations(convsRes.data);
        // ?mode= URL override (used by mode push notifications)
        const params = new URLSearchParams(window.location.search);
        const urlMode = params.get('mode');
        if (urlMode && ['morning', 'decision', 'evening'].includes(urlMode)) {
          setMode(urlMode);
        } else {
          setMode(modeRes.data.mode);
        }
        setVoiceAvailable(voiceRes.data.tts_available);
      } catch (error) {
        console.error('Init error:', error);
      }
    };
    init();
  }, [api]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const loadConversation = async (convId) => {
    setIsLoading(true);
    try {
      const response = await api.get(`/api/conversations/${convId}`);
      setCurrentConversation(response.data);
      setMessages(response.data.messages);
      setMode(response.data.mode);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewConversation = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/api/conversations', { mode });
      setCurrentConversation(response.data);
      setMessages(response.data.messages);
      setConversations(prev => [response.data, ...prev]);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sendMessage = async (content = inputValue) => {
    if (!content.trim() || isSending) return;

    const messageContent = content.trim();
    setInputValue('');
    setIsSending(true);

    if (!currentConversation) {
      try {
        const response = await api.post('/api/conversations', { 
          mode, 
          initial_message: messageContent 
        });
        setCurrentConversation(response.data);
        setMessages(response.data.messages);
        setConversations(prev => [response.data, ...prev]);
      } catch (error) {
        console.error('Failed to create conversation:', error);
        setInputValue(messageContent);
        toast.error('Échec de la création de la conversation. Réessaie.');
      } finally {
        setIsSending(false);
      }
      return;
    }

    // Give the user message a stable id so we can tag it "failed" if the call errors out
    const tempId = `u-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const userMessage = { id: tempId, role: 'user', content: messageContent, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);

    try {
      const response = await api.post('/api/chat', {
        conversation_id: currentConversation.id,
        content: messageContent
      }, { timeout: 110000 });
      // Clear any previous failed flag on that message (success) + append ATLAS reply
      setMessages(prev => {
        const cleaned = prev.map(m => m.id === tempId ? { ...m, failed: false } : m);
        return [...cleaned, response.data];
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      // KEEP the user's message visible, tag it as failed, expose retry
      setMessages(prev => prev.map(m => m.id === tempId ? { ...m, failed: true } : m));
      const isTimeout = error?.code === 'ECONNABORTED' || String(error?.message || '').toLowerCase().includes('timeout');
      toast.error(
        isTimeout
          ? 'La réponse met trop de temps à arriver. Clique sur "Réessayer" sous ton message.'
          : 'Échec de l\'envoi. Clique sur "Réessayer" sous ton message.'
      );
    } finally {
      setIsSending(false);
    }
  };

  const retryMessage = async (failedMessage) => {
    if (isSending) return;
    // Remove the failed message optimistically — sendMessage will re-add it cleanly
    setMessages(prev => prev.filter(m => m.id !== failedMessage.id));
    await sendMessage(failedMessage.content);
  };

  const changeMode = async (newMode) => {
    setMode(newMode);
    if (currentConversation) {
      try {
        await api.put(`/api/conversations/${currentConversation.id}/mode`, { mode: newMode });
      } catch (error) {
        console.error('Failed to update mode:', error);
      }
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      audioChunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        
        try {
          const formData = new FormData();
          formData.append('audio_file', audioBlob, 'recording.webm');
          const response = await api.post('/api/voice/stt', formData);
          if (response.data.text) {
            setInputValue(response.data.text);
          }
        } catch (error) {
          console.error('STT error:', error);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Recording error:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const playTTS = async (text) => {
    if (!voiceAvailable || isPlaying) return;
    
    setIsPlaying(true);
    try {
      const response = await api.post('/api/voice/tts', { text });
      if (response.data.audio_url) {
        if (audioRef.current) {
          audioRef.current.pause();
        }
        audioRef.current = new Audio(response.data.audio_url);
        audioRef.current.onended = () => setIsPlaying(false);
        audioRef.current.onerror = () => setIsPlaying(false);
        await audioRef.current.play();
      }
    } catch (error) {
      console.error('TTS error:', error);
      setIsPlaying(false);
    }
  };

  const stopTTS = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsPlaying(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getModeIcon = (m) => {
    switch (m) {
      case 'morning': return <Sun className="w-4 h-4" />;
      case 'evening': return <Moon className="w-4 h-4" />;
      default: return <Zap className="w-4 h-4" />;
    }
  };

  const getModeColor = (m) => {
    switch (m) {
      case 'morning': return 'text-atlasYellow border-atlasYellow bg-atlasYellow/10';
      case 'evening': return 'text-atlasPurple border-atlasPurple bg-atlasPurple/10';
      default: return 'text-atlasCyan border-atlasCyan bg-atlasCyan/10';
    }
  };

  const chatSuggestions = trans?.chat?.suggestions || {};

  return (
    <div data-testid="chat-page" className="h-full flex">
      {/* Sidebar - Conversations */}
      <div className={`${showSidebar ? 'w-72' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-atlasBorder bg-atlasCard/30`}>
        <div className="p-4 border-b border-atlasBorder">
          <Button 
            data-testid="new-conversation-btn"
            onClick={createNewConversation}
            className="w-full btn-primary flex items-center justify-center gap-2"
            disabled={isLoading}
          >
            <Plus className="w-4 h-4" />
            {t('chat.newSession')}
          </Button>
        </div>
        <ScrollArea className="h-[calc(100%-65px)]">
          <div className="p-2">
            {conversations.map((conv) => (
              <button
                key={conv.id}
                data-testid={`conversation-${conv.id}`}
                onClick={() => loadConversation(conv.id)}
                className={`w-full p-3 rounded-lg mb-1 text-left transition-all ${
                  currentConversation?.id === conv.id 
                    ? 'bg-atlasCyan/10 border border-atlasCyan/30' 
                    : 'hover:bg-white/5'
                }`}
              >
                <div className="flex items-center gap-2 mb-1">
                  <MessageSquare className="w-4 h-4 text-atlasTextSecondary" />
                  <span className={`text-xs px-2 py-0.5 rounded ${getModeColor(conv.mode)}`}>
                    {t(`dashboard.modeName.${conv.mode}`)}
                  </span>
                </div>
                <p className="text-sm text-white truncate">{conv.preview || t('chat.newSession')}</p>
                <p className="text-xs text-atlasTextSecondary mt-1">
                  {format(new Date(conv.created_at), 'MMM d, HH:mm')}
                </p>
              </button>
            ))}
          </div>
        </ScrollArea>
      </div>

      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-atlasBorder flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(!showSidebar)}
              className="p-2 hover:bg-white/5 rounded-lg lg:hidden"
            >
              <ChevronLeft className={`w-5 h-5 text-atlasTextSecondary transition-transform ${!showSidebar ? 'rotate-180' : ''}`} />
            </button>
            <h1 className="atlas-brand text-xl text-atlasCyan tracking-wider">ATLAS</h1>
          </div>

          {/* Mode Selector */}
          <div className="flex items-center gap-2">
            {['morning', 'decision', 'evening'].map((m) => (
              <button
                key={m}
                data-testid={`chat-mode-${m}-btn`}
                onClick={() => changeMode(m)}
                className={`mode-btn flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm transition-all ${
                  mode === m ? getModeColor(m) : 'border-atlasBorder text-atlasTextSecondary hover:border-white/20'
                }`}
              >
                {getModeIcon(m)}
                <span className="hidden sm:inline">{t(`dashboard.modeName.${m}`)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <ScrollArea className="flex-1 p-4">
          <div className="max-w-3xl mx-auto space-y-4">
            {messages.length === 0 && !isLoading && (
              <div className="text-center py-12">
                <h2 className="atlas-brand text-3xl text-white mb-4 tracking-wider">ATLAS</h2>
                <p className="text-atlasTextSecondary mb-8">
                  {t(`chat.emptyState.${mode}`)}
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {(chatSuggestions[mode] || []).map((s, i) => (
                    <button
                      key={i}
                      data-testid={`suggestion-${i}`}
                      onClick={() => sendMessage(s)}
                      className="px-4 py-2 rounded-lg border border-atlasBorder text-atlasTextSecondary hover:border-atlasCyan hover:text-atlasCyan transition-all text-sm"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {messages.map((msg, i) => (
              <div
                key={msg.id || i}
                data-testid={`message-${i}`}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in`}
              >
                <div className={`max-w-[80%] p-4 ${msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-atlas'} ${msg.failed ? 'border border-red-500/40' : ''}`}>
                  <p className="text-white whitespace-pre-wrap">{msg.content}</p>
                  {msg.role === 'user' && msg.failed && (
                    <div className="mt-2 flex items-center justify-between gap-3 pt-2 border-t border-red-500/20">
                      <span className="flex items-center gap-1 text-xs text-red-400">
                        <AlertCircle className="w-3.5 h-3.5" />
                        Échec — message non envoyé
                      </span>
                      <button
                        data-testid={`retry-msg-${i}`}
                        onClick={() => retryMessage(msg)}
                        disabled={isSending}
                        className="flex items-center gap-1 text-xs text-atlasCyan hover:text-white disabled:opacity-50 font-medium"
                      >
                        <RotateCcw className="w-3.5 h-3.5" />
                        Réessayer
                      </button>
                    </div>
                  )}
                  {msg.role === 'assistant' && (
                    <div className="mt-2 flex items-center gap-3">
                      {voiceAvailable && (
                        <button
                          data-testid={`listen-btn-${i}`}
                          onClick={() => isPlaying ? stopTTS() : playTTS(msg.content)}
                          className="flex items-center gap-1 text-xs text-atlasCyan hover:text-atlasCyan/80"
                        >
                          {isPlaying ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                          {isPlaying ? t('chat.stop') : t('chat.listen')}
                        </button>
                      )}
                      <button
                        data-testid={`save-insight-btn-${i}`}
                        onClick={() => setInsightModal({ open: true, content: msg.content.slice(0, 500) })}
                        className="flex items-center gap-1 text-xs text-atlasYellow hover:text-atlasYellow/80"
                      >
                        <Bookmark className="w-3 h-3" />
                        {t('chat.saveInsight')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isSending && (
              <div className="flex justify-start animate-fade-in">
                <div className="chat-bubble-atlas p-4">
                  <div className="flex items-center gap-2 text-atlasCyan">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{t('chat.thinking')}</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        {/* Input Area */}
        <div className="p-4 border-t border-atlasBorder">
          <div className="max-w-3xl mx-auto">
            <div className="glass-card p-2 flex items-center gap-2">
              <button
                data-testid="voice-input-btn"
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onMouseLeave={stopRecording}
                className={`p-3 rounded-lg transition-all ${
                  isRecording 
                    ? 'bg-atlasRed text-white voice-btn-recording' 
                    : 'hover:bg-white/5 text-atlasTextSecondary'
                }`}
              >
                {isRecording ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
              </button>

              <input
                ref={inputRef}
                data-testid="chat-input"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={isRecording ? t('chat.recording') : t('chat.placeholder')}
                className="flex-1 bg-transparent border-none outline-none text-white placeholder-atlasTextSecondary px-2"
                disabled={isRecording}
              />

              <Button
                data-testid="send-message-btn"
                onClick={() => sendMessage()}
                disabled={!inputValue.trim() || isSending}
                className="p-3 bg-atlasCyan text-atlasBg rounded-lg hover:bg-atlasCyan/80 disabled:opacity-50"
              >
                <Send className="w-5 h-5" />
              </Button>
            </div>
            <p className="text-xs text-atlasTextSecondary text-center mt-2">
              {t('chat.inputHint')}
            </p>
          </div>
        </div>
      </div>

      {/* Save Insight Modal */}
      <Dialog open={insightModal.open} onOpenChange={(open) => setInsightModal({ ...insightModal, open })}>
        <DialogContent className="glass-card border-atlasBorder sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl text-white flex items-center gap-2">
              <Bookmark className="w-5 h-5 text-atlasYellow" />
              {t('insight.title')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <label className="text-atlasTextSecondary text-sm">{t('insight.category')}</label>
              <Select value={insightCategory} onValueChange={setInsightCategory}>
                <SelectTrigger className="input-atlas mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="decision">{t('insight.categories.decision')}</SelectItem>
                  <SelectItem value="learning">{t('insight.categories.learning')}</SelectItem>
                  <SelectItem value="pattern">{t('insight.categories.pattern')}</SelectItem>
                  <SelectItem value="goal">{t('insight.categories.goal')}</SelectItem>
                  <SelectItem value="warning">{t('insight.categories.warning')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-atlasTextSecondary text-sm">{t('insight.content')}</label>
              <Textarea
                value={insightModal.content}
                onChange={(e) => setInsightModal({ ...insightModal, content: e.target.value })}
                className="input-atlas mt-1 min-h-[120px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setInsightModal({ open: false, content: '' })} className="text-atlasTextSecondary">
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={async () => {
                setSavingInsight(true);
                try {
                  await api.post('/api/insights', {
                    content: insightModal.content,
                    category: insightCategory,
                    importance: 'medium'
                  });
                  toast.success(t('insight.saved'));
                  setInsightModal({ open: false, content: '' });
                } catch (error) {
                  toast.error(t('insight.failed'));
                } finally {
                  setSavingInsight(false);
                }
              }}
              disabled={savingInsight || !insightModal.content.trim()}
              className="btn-primary"
            >
              {savingInsight ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Check className="w-4 h-4 mr-1" /> {t('common.save')}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Chat;
